import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { userRole } = useAuth();

  const getTheme = () => {
    if (userRole === 'customer') {
      return {
        primary: 'bg-emerald-600',
        secondary: 'bg-emerald-700',
        accent: 'bg-emerald-400',
        light: 'bg-emerald-50',
        dark: 'bg-emerald-900',
        text: {
          primary: 'text-emerald-600',
          secondary: 'text-emerald-700',
          accent: 'text-emerald-400',
          light: 'text-emerald-50',
          dark: 'text-emerald-900',
          white: 'text-white'
        },
        border: {
          primary: 'border-emerald-600',
          secondary: 'border-emerald-700',
          accent: 'border-emerald-400'
        },
        hover: {
          primary: 'hover:bg-emerald-600',
          secondary: 'hover:bg-emerald-700',
          accent: 'hover:bg-emerald-400'
        },
        gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600',
        button: 'bg-emerald-600 text-white hover:bg-emerald-700',
        cardBg: 'bg-white',
        headerBg: 'bg-gradient-to-r from-emerald-600 to-teal-600'
      };
    } else if (userRole === 'business') {
      return {
        primary: 'bg-indigo-600',
        secondary: 'bg-indigo-700',
        accent: 'bg-indigo-400',
        light: 'bg-indigo-50',
        dark: 'bg-indigo-900',
        text: {
          primary: 'text-indigo-600',
          secondary: 'text-indigo-700',
          accent: 'text-indigo-400',
          light: 'text-indigo-50',
          dark: 'text-indigo-900',
          white: 'text-white'
        },
        border: {
          primary: 'border-indigo-600',
          secondary: 'border-indigo-700',
          accent: 'border-indigo-400'
        },
        hover: {
          primary: 'hover:bg-indigo-600',
          secondary: 'hover:bg-indigo-700',
          accent: 'hover:bg-indigo-400'
        },
        gradient: 'bg-gradient-to-r from-indigo-600 to-purple-600',
        button: 'bg-indigo-600 text-white hover:bg-indigo-700',
        cardBg: 'bg-white',
        headerBg: 'bg-gradient-to-r from-indigo-600 to-purple-600'
      };
    }
    
    // Default theme
    return {
      primary: 'bg-blue-600',
      secondary: 'bg-blue-700',
      accent: 'bg-blue-400',
      light: 'bg-blue-50',
      dark: 'bg-blue-900',
      text: {
        primary: 'text-blue-600',
        secondary: 'text-blue-700',
        accent: 'text-blue-400',
        light: 'text-blue-50',
        dark: 'text-blue-900',
        white: 'text-white'
      },
      border: {
        primary: 'border-blue-600',
        secondary: 'border-blue-700',
        accent: 'border-blue-400'
      },
      hover: {
        primary: 'hover:bg-blue-600',
        secondary: 'hover:bg-blue-700',
        accent: 'hover:bg-blue-400'
      },
      gradient: 'bg-gradient-to-r from-blue-600 to-blue-700',
      button: 'bg-blue-600 text-white hover:bg-blue-700',
      cardBg: 'bg-white',
      headerBg: 'bg-gradient-to-r from-blue-600 to-blue-700'
    };
  };

  const value = {
    theme: getTheme(),
    userRole
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
